import { useEffect, useState } from 'react';
import {
  useForm,
  type DefaultValues,
  type FieldValues,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';

import type { UseCatalogWriteResult } from './useCatalogWrite';
import type { FieldServerError } from '../_components/NameField';

/**
 * The edit form of one make or model (AUT-437), shared by the drawer and the
 * model dialog.
 *
 * - `values` are the server's current values; whenever the cached row
 *   changes (a refetch after an alias write, a stale 409, another tab), the
 *   form re-seeds with `keepDirtyValues` — untouched fields follow the
 *   server, what the admin typed stays.
 * - `bodyOf` builds the PATCH from the form and the CURRENT row (its
 *   `updatedAt` verbatim) and returns `null` when nothing changed — Save is
 *   disabled then, and while `syncing` (the detail is refetching, so its
 *   `updatedAt` may be about to move).
 * - A 409 `catalog_stale_write` raises `stale` (a banner that stays until the
 *   next successful save); the runner has already refetched.
 */
export interface EntityForm<V extends FieldValues> {
  form: UseFormReturn<V>;
  stale: boolean;
  serverError: FieldServerError | null;
  clearServerError: () => void;
  canSave: boolean;
  save: () => Promise<boolean>;
}

export interface EntityFormOptions<V extends FieldValues, B, R> {
  /** `zodResolver(schema)` — built by the caller (keeps the zod generics concrete). */
  resolver: Resolver<V>;
  values: V;
  /** Identity of the cached row — a new object means fresh server data. */
  row: object;
  syncing: boolean;
  write: UseCatalogWriteResult;
  bodyOf: (values: V) => B | null;
  send: (body: B) => Promise<R>;
  apply?: (result: R) => void;
  successKey: string;
  onSaved?: (body: B) => void;
}

export const useEntityForm = <V extends FieldValues, B, R>(
  o: EntityFormOptions<V, B, R>,
): EntityForm<V> => {
  const [stale, setStale] = useState(false);
  const [serverError, setServerError] = useState<FieldServerError | null>(null);
  const form = useForm<V>({
    resolver: o.resolver,
    mode: 'onChange',
    defaultValues: o.values as DefaultValues<V>,
  });

  useEffect(() => {
    form.reset(o.values, { keepDirtyValues: true });
    // Re-seed only when the cached row object changes (structural sharing
    // keeps it stable across identical refetches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.row]);

  const body = o.bodyOf(form.watch());
  const canSave = body !== null && !o.syncing && !o.write.busy;

  const save = async (): Promise<boolean> => {
    const current = o.bodyOf(form.getValues());
    if (current === null || o.syncing) return false;
    const outcome = await o.write.run(() => o.send(current), {
      successKey: o.successKey,
      ...(o.apply ? { apply: o.apply } : {}),
    });
    if (outcome.ok) {
      setStale(false);
      form.reset(form.getValues());
      o.onSaved?.(current);
      return true;
    }
    const { error } = outcome;
    if (error.kind === 'stale') setStale(true);
    if (error.kind === 'conflict' || error.kind === 'tooLong') setServerError(error);
    return false;
  };

  return {
    form,
    stale,
    serverError,
    clearServerError: () => setServerError(null),
    canSave,
    save,
  };
};
